import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import { resolveApiBaseList } from '../utils/apiBase';

const ConsultaCalificacionesDocente = () => {
	const [docente, setDocente] = useState(null);
	const [grupos, setGrupos] = useState([]);
	const [grupoSeleccionado, setGrupoSeleccionado] = useState('');
	const [modoConsulta, setModoConsulta] = useState('grupo'); // 'grupo' | 'individual'
	const [alumnoSeleccionado, setAlumnoSeleccionado] = useState('');
	const [datosConsulta, setDatosConsulta] = useState(null);
	const [loadingInicial, setLoadingInicial] = useState(true);
	const [loadingConsulta, setLoadingConsulta] = useState(false);
	const [error, setError] = useState('');

	const apiBases = useMemo(() => resolveApiBaseList(), []);

	const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
		let lastError = null;

		for (const base of apiBases) {
			const normalizedBase = base.replace(/\/$/, '');
			const normalizedPath = path.startsWith('/') ? path : `/${path}`;
			const url = `${normalizedBase}${normalizedPath}`;

			try {
				const response = await fetch(url, options);
				const text = await response.text();

				if (!response.ok) {
					let message = text || `Error ${response.status}`;
					try {
						const parsed = text ? JSON.parse(text) : null;
						if (parsed && typeof parsed === 'object') {
							message = parsed.message || parsed.error || message;
						}
					} catch {
						// mantener mensaje original
					}

					const error = new Error(message);
					error.status = response.status;
					lastError = error;
					console.warn(`Warning: petición fallida a ${url}:`, error);
					continue;
				}

				const trimmed = text.trim();
				if (!trimmed) {
					return { data: {}, baseUrl: normalizedBase };
				}

				try {
					return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
				} catch (parseErr) {
					lastError = new Error(`Respuesta inválida desde ${url}`);
					console.warn(`Warning: no se pudo parsear JSON desde ${url}`, parseErr);
				}
			} catch (error) {
				lastError = error;
				console.warn(`Warning: error de red al solicitar ${url}:`, error);
			}
		}

		throw lastError || new Error('No se pudo contactar al servidor.');
	}, [apiBases]);


	const obtenerDatosUsuario = () => {
		const idSession = sessionStorage.getItem('idUsuario');
		const idLocal = localStorage.getItem('idUsuario');
		const idUsuario = idSession || idLocal;

		if (!idUsuario) {
			console.error('❌ No se encontró idUsuario en el almacenamiento.');
			setError('No se encontró información del usuario. Por favor, inicia sesión nuevamente.');
			return null;
		}

		return { idUsuario };
	};

	const cargarInicial = useCallback(async () => {
		try {
			setLoadingInicial(true);
			const datosUsuario = obtenerDatosUsuario();
			if (!datosUsuario) {
				return;
			}

			const { data: docenteData } = await fetchJsonWithFallback(`/calificaciones/docente-info?idUsuario=${datosUsuario.idUsuario}`);
			if (!docenteData?.docente) {
				throw new Error('La respuesta del servidor no contiene la información del docente.');
			}

			setDocente(docenteData.docente);

			const { data: gruposData } = await fetchJsonWithFallback(`/calificaciones/mis-grupos-calificaciones?idDocente=${docenteData.docente.IdDocente}`);
			setGrupos(Array.isArray(gruposData?.grupos) ? gruposData.grupos : []);
		} catch (err) {
			console.error('💥 Error al cargar datos iniciales del docente:', err);
			setError(err.message || 'Ocurrió un error al cargar los datos iniciales.');
		} finally {
			setLoadingInicial(false);
		}
}, [fetchJsonWithFallback]);

	useEffect(() => {
		cargarInicial();
	}, [cargarInicial]);

	const cargarConsulta = useCallback(
		async ({ grupoId, alumnoId = '' }) => {
			if (!docente?.IdDocente || !grupoId) {
				return;
			}

			try {
				setLoadingConsulta(true);
				setError('');

				const params = new URLSearchParams({
					idDocente: docente.IdDocente,
					grupoId
				});
				if (alumnoId) {
					params.set('alumnoId', alumnoId);
				}

				const { data } = await fetchJsonWithFallback(`/calificaciones/docente/consultar?${params.toString()}`);
				if (!data?.success) {
					throw new Error(data?.message || 'La respuesta de la API indica un error.');
				}

				setDatosConsulta(data);
			} catch (err) {
				console.error('💥 Error al consultar calificaciones del docente:', err);
				setDatosConsulta(null);
				setError(err.message || 'Ocurrió un error al realizar la consulta.');
			} finally {
				setLoadingConsulta(false);
			}
		},
		[docente, fetchJsonWithFallback]
	);

	useEffect(() => {
		if (!grupoSeleccionado) {
			setDatosConsulta(null);
			setAlumnoSeleccionado('');
			return;
		}

		if (modoConsulta === 'grupo') {
			cargarConsulta({ grupoId: grupoSeleccionado });
			setAlumnoSeleccionado('');
		} else if (modoConsulta === 'individual' && alumnoSeleccionado) {
			cargarConsulta({ grupoId: grupoSeleccionado, alumnoId: alumnoSeleccionado });
		} else {
			cargarConsulta({ grupoId: grupoSeleccionado });
		}
	}, [grupoSeleccionado, modoConsulta, alumnoSeleccionado, cargarConsulta]);

	const alumnosDisponibles = useMemo(() => {
		if (!datosConsulta?.alumnos) {
			return [];
		}

		return datosConsulta.alumnos.map((alumno) => ({
			id: alumno.IdAlumno,
			nombre: alumno.NombreCompleto
		}));
	}, [datosConsulta]);

	const trimestres = useMemo(() => datosConsulta?.trimestres ?? [], [datosConsulta]);

	const categorias = useMemo(() => datosConsulta?.categorias ?? [], [datosConsulta]);

	const handleGrupoChange = useCallback((event) => {
		const nuevoGrupo = event.target.value;
		setGrupoSeleccionado(nuevoGrupo);
		setAlumnoSeleccionado('');
		setDatosConsulta(null);
	}, []);

	const handleModoConsultaChange = useCallback((event) => {
		const nuevoModo = event.target.value;
		setModoConsulta(nuevoModo);
		if (nuevoModo === 'grupo') {
			setAlumnoSeleccionado('');
		}
	}, []);

	const alumnosMostrar = useMemo(() => {
		if (!datosConsulta?.alumnos) {
			return [];
		}

		if (modoConsulta === 'individual' && alumnoSeleccionado) {
			return datosConsulta.alumnos.filter((alumno) => String(alumno.IdAlumno) === String(alumnoSeleccionado));
		}

		return datosConsulta.alumnos;
	}, [datosConsulta, modoConsulta, alumnoSeleccionado]);

	const renderDetalleAlumno = (alumno) => {
		return (
			<div key={alumno.IdAlumno} className="card mb-4 shadow-sm">
				<div className="card-header bg-primary text-white">
					<h5 className="mb-0">{alumno.NombreCompleto}</h5>
				</div>
				<div className="card-body">
					{categorias.length === 0 ? (
						<p className="text-muted">No hay materias registradas para este docente en el grupo.</p>
					) : (
						categorias.map((categoria) => (
							<div key={categoria.IdCategoriaMateria} className="mb-3">
								<h6 className="fw-bold text-primary">{categoria.Categoria}</h6>
								<div className="table-responsive">
									<table className="table table-sm table-bordered align-middle">
										<thead className="table-light">
											<tr className="text-center">
												<th>Materia</th>
												{trimestres.map((trimestre) => (
													<th key={trimestre}>{`Trimestre ${trimestre}`}</th>
												))}
											</tr>
										</thead>
										<tbody>
											{(categoria.materias || []).map((materia) => (
												<tr key={materia.IdMateria}>
													<td>{materia.NombreMateria}</td>
													{trimestres.map((trimestre) => {
														const dataTrimestre = alumno.trimestres?.[trimestre]?.[materia.NombreMateria];
														return (
															<td key={trimestre}>
																{dataTrimestre?.calificacion !== undefined && dataTrimestre?.calificacion !== null
																	? dataTrimestre.calificacion
																	: '—'}
																<div className="small text-muted">
																	{dataTrimestre?.observaciones ? `Obs: ${dataTrimestre.observaciones}` : ''}
																</div>
																<div className="small text-muted">
																	{dataTrimestre?.sugerencias ? `Sug: ${dataTrimestre.sugerencias}` : ''}
																</div>
																<div className="small text-muted">
																	{`Asist: ${dataTrimestre?.asistencias ?? 0} | Inasist: ${dataTrimestre?.inasistencias ?? 0}`}
																</div>
															</td>
														);
													})}
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						))
					)}
			</div>
			</div>
		);
	};

	return (
		<div className="container py-4">
			<h2 className="text-center mb-4">Consulta de Calificaciones para Docentes</h2>

			{error && (
				<div className="alert alert-danger" role="alert">
					<i className="fa fa-exclamation-triangle me-2" />
					{error}
				</div>
			)}

			{loadingInicial ? (
				<div className="text-center my-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Cargando...</span>
					</div>
					<p className="mt-3">Cargando información del docente...</p>
				</div>
			) : (
				<>
					<div className="card shadow-sm mb-4">
						<div className="card-body">
							<div className="row g-3 align-items-end">
								<div className="col-md-4">
									<label className="form-label">Docente</label>
									<input
										type="text"
										className="form-control"
										value={docente?.NombreCompleto || '—'}
										readOnly
									/>
								</div>
								<div className="col-md-4">
									<label className="form-label">Grupo</label>
									<select
										className="form-select"
										value={grupoSeleccionado}
										onChange={handleGrupoChange}
										disabled={loadingConsulta || grupos.length === 0}
									>
										<option value="">Selecciona un grupo</option>
										{grupos.map((grupo) => (
											<option key={grupo.IdGrupo} value={grupo.IdGrupo}>
												{grupo.GrupoDisplay || `${grupo.Grado ?? ''}° ${grupo.CicloEscolar ? `- ${grupo.CicloEscolar}` : ''}`}
											</option>
										))}
									</select>
								</div>
								<div className="col-md-4">
									<label className="form-label d-block">Modo de consulta</label>
									<div className="btn-group" role="group" aria-label="Modo de consulta">
										<input
											type="radio"
											className="btn-check"
											name="modoConsulta"
											id="modoGrupo"
											value="grupo"
											checked={modoConsulta === 'grupo'}
											onChange={handleModoConsultaChange}
											disabled={loadingConsulta}
										/>
										<label className="btn btn-outline-primary" htmlFor="modoGrupo">
											Grupo completo
										</label>

										<input
											type="radio"
											className="btn-check"
											name="modoConsulta"
											id="modoIndividual"
											value="individual"
											checked={modoConsulta === 'individual'}
											onChange={handleModoConsultaChange}
											disabled={loadingConsulta}
										/>
										<label className="btn btn-outline-primary" htmlFor="modoIndividual">
											Por alumno
										</label>
									</div>
								</div>
							</div>

							{modoConsulta === 'individual' && (
								<div className="row mt-3">
									<div className="col-md-6">
										<label className="form-label">Alumno</label>
										<select
											className="form-select"
											value={alumnoSeleccionado}
											onChange={(event) => setAlumnoSeleccionado(event.target.value)}
											disabled={loadingConsulta || !grupoSeleccionado || alumnosDisponibles.length === 0}
										>
											<option value="">Selecciona un alumno</option>
											{alumnosDisponibles.map((alumno) => (
												<option key={alumno.id} value={alumno.id}>
													{alumno.nombre}
												</option>
											))}
										</select>
										{!loadingConsulta && grupoSeleccionado && alumnosDisponibles.length === 0 && (
											<small className="text-muted">No hay alumnos disponibles en este grupo.</small>
										)}
									</div>
								</div>
							)}
						</div>
					</div>

					{loadingConsulta && (
						<div className="text-center my-5">
							<div className="spinner-border text-secondary" role="status">
								<span className="visually-hidden">Cargando...</span>
							</div>
							<p className="mt-3">Consultando calificaciones...</p>
						</div>
					)}

					{!loadingConsulta && datosConsulta && (
						<>
							<div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
								<div>
									<strong>Resultados:</strong>{' '}
									{modoConsulta === 'grupo'
										? `${alumnosMostrar.length} alumno(s) en el grupo`
										: alumnoSeleccionado
											? `Información individual del alumno seleccionado`
											: 'Selecciona un alumno para ver sus detalles'}
								</div>
							</div>

							<div className="reporte-contenido">
								{alumnosMostrar.length === 0 ? (
									<div className="alert alert-warning">Selecciona un alumno para visualizar los detalles.</div>
								) : (
									alumnosMostrar.map((alumno) => renderDetalleAlumno(alumno))
								)}
							</div>
						</>
					)}

					{!loadingConsulta && !datosConsulta && grupoSeleccionado && !error && (
						<div className="alert alert-info">No se encontraron calificaciones registradas para esta combinación de búsqueda.</div>
					)}
				</>
			)}
		</div>
	);
};

export default ConsultaCalificacionesDocente;
